import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output, Renderer2 } from '@angular/core';
import { HxIcon } from '../icon/icon';
import type { ColumnAlignment } from '../table-grid/types';
import { HxTooltipDirective, TooltipColor, TooltipPosition } from '../tooltip/tooltip';
import { HxTableComponent } from './table';

/**
 * Parses width input to a valid CSS width string.
 *
 * - Numbers are treated as pixels: 200 → '200px'
 * - Strings pass through as-is: '25%', '1fr', '200px', 'auto'
 */
function parseWidth(value: number | string | undefined): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return `${value}px`;
  }
  return value;
}

/**
 * Header cell component
 *
 * @example
 *   ```html
 *   <hx-header-cell [sortable]="true" (sort)="onSort()">
 *     Name
 *   </hx-header-cell>
 *   ```;
 *
 * @example
 *   ```html
 *   <!-- With percentage width -->
 *   <hx-header-cell [width]="'25%'">Name</hx-header-cell>
 *
 *   <!-- With pixel width (number or string) -->
 *   <hx-header-cell [width]="200">Age</hx-header-cell>
 *   <hx-header-cell [width]="'200px'">Score</hx-header-cell>
 *
 *   <!-- With fr units -->
 *   <hx-header-cell [width]="'2fr'">Description</hx-header-cell>
 *   ```;
 */
@Component({
  selector: 'hx-header-cell',
  standalone: true,
  imports: [HxIcon, HxTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="hx-header-cell__text">
      <div [hxTooltip]="getTooltipText()" [position]="tooltipPosition()" [tooltipColor]="tooltipColor()">
        <ng-content />
      </div>
    </div>
    @if (sortable()) {
      <hx-icon
        class="hx-header-cell__sort-icon"
        [svgIcon]="getSortIcon()"
        size="12"
        (click)="handleClick()"
        tabindex="0"
        role="button"
        [attr.aria-label]="'Sort by ' + (columnName() || 'column')" />
    }
  `,
  host: {
    class: 'hx-header-cell',
    role: 'columnheader',
    '[class.hx-header-cell--sortable]': 'sortable()',
    '[class.hx-header-cell--sticky]': 'sticky()',
    '[class.hx-header-cell--align-left]': 'align() === "left"',
    '[class.hx-header-cell--align-center]': 'align() === "center"',
    '[class.hx-header-cell--align-right]': 'align() === "right"',
    '[class.resizable]': 'tableComponent?.resizableColumns()',
    '[class.resizing]': 'isResizing',
    '[attr.data-sort]': 'sortDirection()',
    '[style.background-color]': 'backgroundColor()',
    '[style.color]': 'color()',
    '(mousedown)': 'onMouseDown($event)',
  },
})
export class HxHeaderCell {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  readonly tableComponent = inject(HxTableComponent, { optional: true });

  isResizing = false;
  private startX = 0;
  private startWidth = 0;
  private columnWidths: number[] = [];
  private resizedWidths: string[] = [];
  private mouseMoveListener?: () => void;
  private mouseUpListener?: () => void;

  /**
   * Column width - accepts number (pixels) or string (any CSS unit).
   *
   * @example
   *   - [width]="200" → '200px'
   *   - [width]="'25%'" → '25%'
   *   - [width]="'2fr'" → '2fr'
   *   - [width]="'auto'" → 'auto'
   */
  readonly width = input<number | string>();

  /** Gets the width as a valid CSS string. Numbers are converted to pixels, strings pass through as-is. */
  getCssWidth(): string | undefined {
    return parseWidth(this.width());
  }

  /** Whether column is sortable */
  readonly sortable = input<boolean>(false);

  /** Whether column is sticky */
  readonly sticky = input<boolean>(false);

  /** Sort direction: 'asc', 'desc', or null */
  readonly sortDirection = input<'asc' | 'desc' | null>(null);

  /** Column name for sort events */
  readonly columnName = input<string>();

  readonly tooltip = input<string>('');
  readonly tooltipPosition = input<TooltipPosition>('top');
  readonly tooltipColor = input<TooltipColor>('info');

  /** Custom background color */
  readonly backgroundColor = input<string>();

  /** Custom text color */
  readonly color = input<string>();

  /** Content alignment: 'left' | 'center' | 'right' */
  readonly align = input<ColumnAlignment>('left');

  /** Emits when sort is requested with column name and direction */
  readonly sort = output<'asc' | 'desc' | null>();

  getSortIcon(): string {
    const direction = this.sortDirection();
    if (direction === 'asc') {
      return 'arrow-up';
    }
    if (direction === 'desc') {
      return 'arrow-down';
    }
    return 'arrow-down-up';
  }

  getTooltipText(): string {
    return this.tooltip() || '';
  }

  handleClick(): void {
    if (!this.sortable()) {
      return;
    }

    const current = this.sortDirection();
    let next: 'asc' | 'desc' | null;

    if (!current) {
      next = 'asc';
    } else if (current === 'asc') {
      next = 'desc';
    } else {
      next = null;
    }

    this.sort.emit(next);
  }

  onMouseDown(event: MouseEvent): void {
    if (!this.tableComponent?.resizableColumns()) {
      return;
    }

    const element = this.elementRef.nativeElement as HTMLElement;
    const rect = element.getBoundingClientRect();
    const isNearRightEdge = event.clientX > rect.right - 8;

    if (!isNearRightEdge) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = element.getBoundingClientRect().width;

    // Store all current column widths
    const headerElement = element.parentElement;
    if (headerElement) {
      const cells = Array.from(headerElement.children) as HTMLElement[];
      this.columnWidths = cells.map((cell) => cell.getBoundingClientRect().width);
    }

    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
      this.onMouseMove(e);
    });

    this.mouseUpListener = this.renderer.listen('document', 'mouseup', () => {
      this.onMouseUp();
    });
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }

    event.preventDefault();

    const diff = event.clientX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);

    // Get the header element and all cells
    const element = this.elementRef.nativeElement as HTMLElement;
    const headerElement = element.parentElement;

    if (headerElement && this.tableComponent) {
      const cells = Array.from(headerElement.children) as HTMLElement[];
      const currentIndex = cells.indexOf(element);

      // Build new grid template columns using stored widths
      const widths = this.columnWidths.map((width, index) => {
        const actualWidth = index === currentIndex ? newWidth : width;
        return `${actualWidth}px`;
      });

      this.resizedWidths = widths;
      this.tableComponent.updateGridColumns(widths.join(' '));
    }
  }

  private onMouseUp(): void {
    const wasResizing = this.isResizing;
    this.isResizing = false;

    if (this.mouseMoveListener) {
      this.mouseMoveListener();
    }

    if (this.mouseUpListener) {
      this.mouseUpListener();
    }

    if (wasResizing && this.resizedWidths.length > 0) {
      this.tableComponent?.notifyColumnResizeEnd(this.resizedWidths);
      this.resizedWidths = [];
    }
  }
}
